import { useEffect, useState } from "react";
import OnlineBookingSwitch from "../../components/OnlineBookingSwitch";
import OrganizerPillSelect from "../../components/OrganizerPillSelect";
import OrganizerTimePillSelect from "../../components/OrganizerTimePillSelect";
import "../../components/EventFormPickers.css";
import AvisoSenasBloqueadas from "../../components/AvisoSenasBloqueadas";
import ClubAddressAutocomplete from "../../canchas/ClubAddressAutocomplete";
import ClubLocationPicker from "../../canchas/ClubLocationPicker";
import useCitiesByState from "../../../hooks/useCitiesByState";
import { COUNTRIES } from "../../../pages/reservas/components/ReservaShared";
import {
	ANTELACION_OPCIONES,
	CANCELACION_OPCIONES,
	DIAS_SEMANA,
	DURACIONES_TURNO,
	MAX_GALERIA,
	TOLERANCIA_OPCIONES,
	VENTANA_OPCIONES,
	faltaBar,
	faltaContacto,
	faltaHorarios,
	faltaReserva,
	faltaSalon,
	faltaSenaZona,
	nuevaZona,
	totalesSalon,
	turnosDe,
} from "./barPayload";

const MAX_UPLOAD_BYTES = 10 * 1024 * 1024;
const plata = (n) => `$ ${Number(n || 0).toLocaleString("es-AR")}`;

export const Campo = ({ label, children, hint }) => (
	<div className="ef-field">
		<label className="ef-label">{label}</label>
		{children}
		{hint && <p className="ef-footnote">{hint}</p>}
	</div>
);

/* ---- Ubicación (same block, rules and components as the bar and club forms; reused by Canchas) ---- */
export const BloqueUbicacion = ({
	data: d,
	set,
	states,
	entityLabel = "bar",
}) => {
	const { cities } = useCitiesByState(d.stateid);
	const provincia =
		states.find((s) => String(s._id) === String(d.stateid))?.name || "";
	const ciudad =
		cities.find((c) => String(c._id) === String(d.cityid))?.name || "";
	const consulta = [d.address, ciudad, provincia].filter(Boolean).join(", ");
	return (
		<>
			<div className="pu-grid-2">
				<Campo label="Provincia">
					<OrganizerPillSelect
						placeholder="Sin provincia"
						value={d.stateid}
						// Changing province invalidates the previously chosen city.
						onChange={(val) => set({ stateid: val, cityid: "" })}
						options={[
							{ value: "", label: "Sin provincia" },
							...states.map((s) => ({ value: s._id, label: s.name })),
						]}
					/>
				</Campo>
				<Campo label="Ciudad">
					<OrganizerPillSelect
						placeholder="Sin ciudad"
						value={d.cityid}
						searchable
						disabled={!d.stateid}
						onChange={(val) => set({ cityid: val })}
						options={[
							{ value: "", label: "Sin ciudad" },
							...cities.map((c) => ({ value: c._id, label: c.name })),
						]}
					/>
				</Campo>
			</div>
			<Campo label="Dirección">
				<ClubAddressAutocomplete
					value={d.address}
					// A manual edit invalidates the previous confirmed pin.
					onChange={(value) => set({ address: value, lat: "", lng: "" })}
					onPlaceSelected={({ address, lat, lng }) =>
						set({ address, lat, lng })
					}
				/>
			</Campo>
			<Campo
				label="Ubicación en el mapa"
				hint="Buscamos la dirección automáticamente. Si el pin no cae en el lugar exacto, arrastralo o hacé clic en el mapa para corregirlo."
			>
				<div className="zr-map">
					<ClubLocationPicker
						address={consulta}
						lat={d.lat}
						lng={d.lng}
						entityLabel={entityLabel}
						onChange={({ lat, lng }) => set({ lat, lng })}
					/>
				</div>
			</Campo>
		</>
	);
};

/* ---- 1. Bar + Ubicación ---- */
const PasoBar = ({ data: d, set, states }) => (
	<>
		<Campo
			label="Nombre del bar o restaurante"
			hint={
				d.nombreDelPortal && d.nombre === d.nombreDelPortal
					? "Del portal. Podés cambiarlo."
					: undefined
			}
		>
			<input
				className="ef-input"
				value={d.nombre}
				onChange={(e) => set({ nombre: e.target.value })}
				placeholder="Ej: Nórdico Rooftop"
			/>
		</Campo>
		<BloqueUbicacion data={d} set={set} states={states} entityLabel="bar" />
	</>
);

/* ---- 2. Horarios ---- */
const PasoHorarios = ({ data: d, set }) => {
	const alternar = (weekday) =>
		set({
			dias: d.dias.includes(weekday)
				? d.dias.filter((x) => x !== weekday)
				: [...d.dias, weekday],
		});
	const turnos = turnosDe(d.desde, d.hasta, d.turno);
	return (
		<>
			<div className="pu-grid-2">
				<Campo label="Días que abrís">
					<div className="pu-chips">
						{DIAS_SEMANA.map((x) => (
							<button
								key={x.weekday}
								type="button"
								className="pu-mode"
								style={{ padding: "6px 12px" }}
								aria-pressed={d.dias.includes(x.weekday)}
								aria-label={x.largo}
								onClick={() => alternar(x.weekday)}
							>
								{x.corto}
							</button>
						))}
					</div>
				</Campo>
				<div className="pu-grid-2">
					<Campo label="Abre">
						<OrganizerTimePillSelect
							triggerClassName="ef-date-pill-trigger"
							value={d.desde}
							onChange={(val) => set({ desde: val })}
							quarterHours
						/>
					</Campo>
					<Campo label="Cierra">
						<OrganizerTimePillSelect
							triggerClassName="ef-date-pill-trigger"
							value={d.hasta}
							onChange={(val) => set({ hasta: val })}
							quarterHours
						/>
					</Campo>
				</div>
				<Campo label="Tiempo entre reservas (por mesa)">
					<OrganizerPillSelect
						value={d.turno}
						onChange={(v) => set({ turno: Number(v) })}
						options={DURACIONES_TURNO}
					/>
				</Campo>
				<Campo
					label="Ventana de reserva"
					hint="Con cuántos días de anticipación se puede reservar."
				>
					<OrganizerPillSelect
						value={d.ventana}
						onChange={(v) => set({ ventana: Number(v) })}
						options={VENTANA_OPCIONES}
					/>
				</Campo>
			</div>
			<Campo label="Turnos que se van a ofrecer">
				<div className="pu-chips" aria-live="polite">
					{turnos.length ? (
						turnos.map((t) => (
							<span key={t} className="pu-pill">
								{t}
							</span>
						))
					) : (
						<span className="pu-empty">Revisá el horario</span>
					)}
				</div>
			</Campo>
		</>
	);
};

/* ---- 3. Salón ---- */
const PasoSalon = ({ data: d, set }) => {
	const setZona = (i, patch) =>
		set({ zonas: d.zonas.map((z, j) => (j === i ? { ...z, ...patch } : z)) });
	const t = totalesSalon(d.zonas);
	return (
		<>
			<table className="pu-table">
				<thead>
					<tr>
						<th>Zona</th>
						<th style={{ width: 130 }}>Mesas</th>
						<th style={{ width: 160 }}>Personas por mesa</th>
						<th style={{ width: 40 }} />
					</tr>
				</thead>
				<tbody>
					{d.zonas.map((z, i) => (
						<tr key={i}>
							<td>
								<input
									className="ef-input"
									aria-label="Zona"
									value={z.nombre}
									placeholder="Ej: Interior, Vereda, VIP"
									onChange={(e) => setZona(i, { nombre: e.target.value })}
								/>
							</td>
							<td>
								<input
									className="ef-input"
									type="number"
									min="1"
									max="50"
									aria-label="Mesas"
									value={z.mesas}
									onChange={(e) => setZona(i, { mesas: e.target.value })}
								/>
							</td>
							<td>
								<input
									className="ef-input"
									type="number"
									min="1"
									max="20"
									aria-label="Personas por mesa"
									value={z.cap}
									onChange={(e) => setZona(i, { cap: e.target.value })}
								/>
							</td>
							<td>
								{d.zonas.length > 1 && (
									<button
										type="button"
										className="ef-btn ef-btn--ghost"
										aria-label={`Quitar ${z.nombre || "zona"}`}
										onClick={() =>
											set({ zonas: d.zonas.filter((_, j) => j !== i) })
										}
									>
										×
									</button>
								)}
							</td>
						</tr>
					))}
				</tbody>
			</table>
			<div className="pu-row-actions">
				<button
					type="button"
					className="ef-btn ef-btn--ghost"
					onClick={() => set({ zonas: [...d.zonas, nuevaZona()] })}
				>
					Agregar zona
				</button>
				<span className="pu-pill" aria-live="polite">
					{t.mesas} mesas · {t.lugares} lugares
				</span>
			</div>
		</>
	);
};

/* ---- 4. Cómo se reserva ---- */
const DIAS_TXT = (dias) =>
	dias.length === 7
		? "Todos los días"
		: DIAS_SEMANA.filter((x) => dias.includes(x.weekday))
				.map((x) => x.corto)
				.join(" ");

const PasoReserva = ({ data: d, set, senasBloqueadas }) => {
	return (
		<div className="pu-split">
			<div className="pu-body">
				<OnlineBookingSwitch
					checked={d.onlineBookingEnabled}
					onChange={(onlineBookingEnabled) => set({ onlineBookingEnabled })}
				/>
				<div className="pu-modes">
					<button
						type="button"
						className="pu-mode"
						aria-pressed={d.modo === "libre"}
						onClick={() => set({ modo: "libre" })}
					>
						<b>Sin seña</b>
						<small>Reservan y listo. Te llega el aviso.</small>
					</button>
					<button
						type="button"
						className="pu-mode"
						aria-pressed={d.modo === "sena"}
						disabled={senasBloqueadas}
						onClick={() => set({ modo: "sena" })}
					>
						<b>Cobro anticipado</b>
						<small>
							{senasBloqueadas
								? "Necesitás tus cobros validados."
								: "Pagan una seña al reservar."}
						</small>
					</button>
				</div>
				{senasBloqueadas && <AvisoSenasBloqueadas />}
				<div className="pu-grid-2">
					{d.modo === "sena" && (
						<Campo label="Monto de seña general (ARS)">
							<div className="pu-money">
								<span>$</span>
								<input
									className="ef-input"
									type="number"
									min="0"
									value={d.sena}
									onChange={(e) => set({ sena: e.target.value })}
									placeholder="0"
								/>
							</div>
						</Campo>
					)}
					<Campo
						label="Máximo de personas por reserva"
						hint="Grupos más grandes te escriben por chat."
					>
						<input
							className="ef-input"
							type="number"
							min="1"
							value={d.maxPersonas}
							onChange={(e) => set({ maxPersonas: e.target.value })}
						/>
					</Campo>
				</div>
			</div>
			<aside className="pu-summary" aria-label="Resumen">
				<dl style={{ margin: 0 }}>
					<dt>Lugar</dt>
					<dd>
						{d.nombre || "—"}
						<br />
						{d.address || "—"}
					</dd>
					<dt>Días</dt>
					<dd>{DIAS_TXT(d.dias) || "—"}</dd>
					<dt>Horario</dt>
					<dd>
						{d.desde} a {d.hasta} · turnos de{" "}
						{DURACIONES_TURNO.find((o) => o.value === d.turno)?.label}
					</dd>
					<dt>Salón</dt>
					<dd>
						{d.zonas.map((z, i) => (
							<div key={i}>
								{z.nombre || "—"}: {z.mesas} × {z.cap}
							</div>
						))}
					</dd>
					<dt>Reserva</dt>
					<dd>
						{d.modo === "sena"
							? `Seña ${d.sena ? plata(d.sena) : "—"}`
							: "Sin seña"}{" "}
						· hasta {d.maxPersonas || "—"} personas
					</dd>
				</dl>
			</aside>
		</div>
	);
};

/* ---- Optional: contact + description ---- */
export const PasoContacto = ({
	data: d,
	set,
	lugar = "bar",
	ejemplo = lugar,
	sinDescripcion = false,
}) => {
	const pais = COUNTRIES.find((c) => c.prefix === d.prefijo) || COUNTRIES[0];
	return (
		<>
			{!sinDescripcion && (
				<Campo
					label="Descripción"
					hint="Lo que ven tus clientes en la página de reservas."
				>
					<textarea
						className="ef-textarea"
						style={{ height: 90 }}
						value={d.descripcion}
						onChange={(e) => set({ descripcion: e.target.value })}
						placeholder={`Contá más sobre el ${lugar}...`}
					/>
				</Campo>
			)}
			<div className="pu-grid-2">
				<Campo label="Nro de celular">
					<div className="pu-row-actions">
						<select
							className="ef-input"
							style={{ maxWidth: 130 }}
							aria-label="Prefijo del país"
							value={d.prefijo}
							onChange={(e) => set({ prefijo: e.target.value })}
						>
							{COUNTRIES.map((c) => (
								<option key={c.code} value={c.prefix}>
									{c.label}
								</option>
							))}
						</select>
						<input
							className="ef-input"
							type="tel"
							aria-label="Número de celular"
							value={d.telefono}
							placeholder={pais.placeholder}
							onChange={(e) => set({ telefono: e.target.value })}
						/>
					</div>
				</Campo>
				<Campo label="Email de contacto">
					<input
						className="ef-input"
						type="email"
						value={d.email}
						placeholder={`${ejemplo}@ejemplo.com`}
						onChange={(e) => set({ email: e.target.value })}
					/>
				</Campo>
				<Campo label="Sitio web (opcional)">
					<input
						className="ef-input"
						type="url"
						value={d.web}
						placeholder={`https://www.tu-${ejemplo}.com`}
						onChange={(e) => set({ web: e.target.value })}
					/>
				</Campo>
				<Campo label="Instagram (opcional)">
					<input
						className="ef-input"
						value={d.instagram}
						placeholder="nordico.rooftop"
						onChange={(e) => set({ instagram: e.target.value })}
					/>
				</Campo>
			</div>
		</>
	);
};

/* ---- Optional: photos ---- */
const Miniatura = ({ file, onQuitar }) => {
	const [url, setUrl] = useState("");
	useEffect(() => {
		const u = URL.createObjectURL(file);
		setUrl(u);
		return () => URL.revokeObjectURL(u);
	}, [file]);
	return (
		<div className="ef-upload-tile">
			<div className="ef-dropzone has-image">
				<img src={url} alt={file.name} />
			</div>
			<button type="button" className="pu-link" onClick={onQuitar}>
				Quitar
			</button>
		</div>
	);
};

export const PasoFotos = ({ data: d, set }) => {
	const [error, setError] = useState("");
	const aceptar = (files) => {
		const ok = files.filter((f) => f.size <= MAX_UPLOAD_BYTES);
		setError(
			ok.length < files.length
				? "Alguna imagen supera el máximo de 10 MB y se omitió."
				: "",
		);
		return ok;
	};
	return (
		<>
			<Campo
				label="Imagen de portada"
				hint="Recomendado: 1600 x 900 px. Hasta 10 MB."
			>
				{d.portada ? (
					<Miniatura file={d.portada} onQuitar={() => set({ portada: null })} />
				) : (
					<label
						className="ef-btn ef-btn--outline"
						style={{ width: "fit-content" }}
					>
						Subir imagen de portada
						<input
							type="file"
							accept="image/*"
							hidden
							aria-label="Imagen de portada"
							onChange={(e) => {
								const [f] = aceptar(Array.from(e.target.files || []));
								if (f) set({ portada: f });
							}}
						/>
					</label>
				)}
			</Campo>
			<Campo
				label={`Galería de fotos | ${MAX_GALERIA} máx.`}
				hint="Recomendado: 1080 x 1080 px."
			>
				<div className="pu-photo-grid">
					{d.galeria.map((f, i) => (
						<Miniatura
							key={`${f.name}-${i}`}
							file={f}
							onQuitar={() =>
								set({ galeria: d.galeria.filter((_, j) => j !== i) })
							}
						/>
					))}
				</div>
				{d.galeria.length < MAX_GALERIA && (
					<label
						className="ef-btn ef-btn--outline"
						style={{ width: "fit-content" }}
					>
						Subir fotos
						<input
							type="file"
							accept="image/*"
							multiple
							hidden
							aria-label="Fotos de la galería"
							onChange={(e) =>
								set({
									galeria: [
										...d.galeria,
										...aceptar(Array.from(e.target.files || [])),
									].slice(0, MAX_GALERIA),
								})
							}
						/>
					</label>
				)}
				{error && (
					<p className="ef-hint" style={{ color: "var(--ef-danger)" }}>
						{error}
					</p>
				)}
			</Campo>
		</>
	);
};

/* ---- Optional: policies ---- */
export const PasoPoliticas = ({ data: d, set, conVentana = false }) => (
	<>
		<div className="pu-grid-2">
			{conVentana && (
				<Campo
					label="Ventana de reserva"
					hint="Con cuántos días de anticipación se puede reservar."
				>
					<OrganizerPillSelect
						value={d.ventana}
						onChange={(v) => set({ ventana: Number(v) })}
						options={VENTANA_OPCIONES}
					/>
				</Campo>
			)}
			<Campo label="Anticipación mínima para reservar">
				<OrganizerPillSelect
					value={d.antelacion}
					onChange={(v) => set({ antelacion: Number(v) })}
					options={ANTELACION_OPCIONES}
				/>
			</Campo>
			<Campo label="Tolerancia por llegada tarde">
				<OrganizerPillSelect
					value={d.tolerancia}
					onChange={(v) => set({ tolerancia: Number(v) })}
					options={TOLERANCIA_OPCIONES}
				/>
			</Campo>
			<div className="ef-config-control ef-config-control--switch">
				<span className="ef-switch-text">
					<span className="ef-switch-title">Cancelación de reserva</span>
					<span className="ef-switch-sub">Permitir que el cliente cancele</span>
				</span>
				<label className="ef-switch">
					<input
						type="checkbox"
						aria-label="Cancelación de reserva"
						checked={d.cancela}
						onChange={(e) => set({ cancela: e.target.checked })}
					/>
					<span className="ef-track" />
				</label>
			</div>
			{d.cancela && (
				<Campo label="Plazo de cancelación sin penalidad">
					<OrganizerPillSelect
						value={d.plazo}
						onChange={(v) => set({ plazo: Number(v) })}
						options={CANCELACION_OPCIONES}
					/>
				</Campo>
			)}
		</div>
		<Campo
			label="Notas adicionales para el cliente (opcional)"
			hint="Se muestran en el paso de confirmación."
		>
			<textarea
				className="ef-textarea"
				style={{ height: 70 }}
				value={d.notas}
				onChange={(e) => set({ notas: e.target.value })}
				placeholder="Ej: Se requiere confirmación de llegada, traer documento..."
			/>
		</Campo>
	</>
);

/* ---- Optional: deposit per zone ---- */
const PasoSenaZona = ({ data: d, set }) => {
	const setZona = (i, sena) =>
		set({ zonas: d.zonas.map((z, j) => (j === i ? { ...z, sena } : z)) });
	return (
		<>
			<div className="ef-config-control ef-config-control--switch">
				<span className="ef-switch-text">
					<span className="ef-switch-title">Precio de seña por zona</span>
					<span className="ef-switch-sub">
						Seña general: {d.sena ? plata(d.sena) : "—"}. Las zonas sin precio
						propio usan la general.
					</span>
				</span>
				<label className="ef-switch">
					<input
						type="checkbox"
						aria-label="Precio de seña por zona"
						checked={d.zonePricing}
						onChange={(e) => set({ zonePricing: e.target.checked })}
					/>
					<span className="ef-track" />
				</label>
			</div>
			{d.zonePricing && (
				<table className="pu-table">
					<thead>
						<tr>
							<th>Zona</th>
							<th style={{ width: 200 }}>Seña</th>
						</tr>
					</thead>
					<tbody>
						{d.zonas.map((z, i) => (
							<tr key={i}>
								<td>
									<b>{z.nombre}</b> <small>{z.mesas} mesas</small>
								</td>
								<td>
									<div className="pu-money">
										<span>$</span>
										<input
											className="ef-input"
											type="number"
											min="0"
											aria-label={`Seña de ${z.nombre}`}
											value={z.sena}
											onChange={(e) => setZona(i, e.target.value)}
										/>
									</div>
								</td>
							</tr>
						))}
					</tbody>
				</table>
			)}
			<p className="ef-hint">
				Zoco no cobra cargos por servicio ni por reserva.
			</p>
		</>
	);
};

export const pasosBar = ({ states, senasBloqueadas }) => ({
	req: [
		{
			id: "bar",
			titulo: "Tu bar o restaurante",
			bajada:
				"El nombre viene del portal y lo podés cambiar. La ubicación es la que ven tus clientes en el mapa.",
			render: (p) => <PasoBar {...p} states={states} />,
			falta: faltaBar,
		},
		{
			id: "horarios",
			titulo: "¿Cuándo se puede reservar?",
			bajada:
				"Con el horario y el tiempo de cada reserva armamos los turnos solos.",
			render: (p) => <PasoHorarios {...p} />,
			falta: faltaHorarios,
		},
		{
			id: "salon",
			titulo: "Tu salón",
			bajada:
				"Dividilo en zonas y decí cuántas mesas tiene cada una. Las mesas se crean solas.",
			render: (p) => <PasoSalon {...p} />,
			falta: faltaSalon,
		},
		{
			id: "reserva",
			titulo: "¿Cómo se reserva?",
			final: "Abrir reservas",
			bajada:
				"Podés pedir una seña para que no te dejen la mesa vacía, o tomar reservas sin pago.",
			render: (p) => <PasoReserva {...p} senasBloqueadas={senasBloqueadas} />,
			falta: faltaReserva,
		},
	],
	opc: [
		{
			id: "contacto",
			titulo: "Contacto y descripción",
			seccion: "Información general",
			bajada:
				"Lo que ven tus clientes en la página de reservas para conocerte y escribirte.",
			render: (p) => <PasoContacto {...p} />,
			falta: faltaContacto,
		},
		{
			id: "fotos",
			titulo: "Fotos del lugar",
			seccion: "Información general",
			bajada:
				"La portada encabeza tu página de reservas; la galería muestra el ambiente.",
			render: (p) => <PasoFotos {...p} />,
			falta: () => "",
		},
		{
			id: "politicas",
			titulo: "Políticas de reserva",
			seccion: "Políticas",
			bajada: "Reglas que se aplican a todas las reservas de este bar.",
			render: (p) => <PasoPoliticas {...p} />,
			falta: () => "",
		},
		{
			id: "senaZona",
			titulo: "Seña por zona",
			seccion: "Pagos",
			bajada:
				"Si alguna zona vale más (una terraza, un VIP), cobrá una seña distinta. Las zonas sin precio usan la seña general.",
			si: (d) => d.modo === "sena",
			render: (p) => <PasoSenaZona {...p} />,
			falta: faltaSenaZona,
		},
	],
});
